import { useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import {
  BorrowerType,
  FinancialStatementForm,
  type TFinancialConditionCertificateDto,
  type TFinancialConditionCertificateResponse,
  type TFinancialConditionFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import {
  applyFinancialConditionCertificateGeneralInfo,
  deserializeFinancialConditionCertificate,
  getFinancialStatementFormFromCertificate,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/mapDtoToFormValues.ts';
import {
  getFinancialConditionDefaultValues,
  mergeFinancialStatementFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import { collectHistoricalPrefilledFieldNames } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/historicalPrefill/utils.ts';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import type {
  TDocumentGenerationBank,
  TDocumentGenerationCurrency,
} from '~/store/covenants/types.ts';

const HISTORICAL_READ_ONLY_FIELDS = [
  'borrowerType',
  'financialStatementForm',
  'borrowerName',
  'borrowerCode',
  'reportPeriod',
  'reportYear',
  'reportDate',
];

type TLoadedCertificate = {
  requestId: number;
  borrowerType: BorrowerType;
  certificate: TFinancialConditionCertificateDto;
  response: TFinancialConditionCertificateResponse;
  financialStatementForm?: FinancialStatementForm;
  localValues?: TFinancialConditionFormValues;
};

type TUseFinancialConditionCertificateLoadingParams = {
  form: UseFormReturn<TFinancialConditionFormValues>;
  banks: TDocumentGenerationBank[];
  currencies: TDocumentGenerationCurrency[];
  isDictionariesReady: boolean;
  isLegalEntity: boolean;
  financialStatementForm?: FinancialStatementForm;
  financialStatementFormOption: TAutocompleteOption | null;
  historicalBody?: unknown;
  isHistoricalSource?: boolean;
  onLoadFinancialCondition?: () => Promise<TFinancialConditionCertificateResponse | null>;
};

export const useFinancialConditionCertificateLoading = ({
  form,
  banks,
  currencies,
  isDictionariesReady,
  isLegalEntity,
  financialStatementForm,
  financialStatementFormOption,
  historicalBody,
  isHistoricalSource,
  onLoadFinancialCondition,
}: TUseFinancialConditionCertificateLoadingParams) => {
  const hasLoadedCertificateRef = useRef(false);
  const loadedFinancialStatementFormRef = useRef<FinancialStatementForm | null>(null);
  const certificateRequestIdRef = useRef(0);
  const certificateVersionRef = useRef<number | null>(null);
  const [isLoadingCertificate, setIsLoadingCertificate] = useState(
    Boolean(onLoadFinancialCondition)
  );
  const [loadedCertificate, setLoadedCertificate] = useState<TLoadedCertificate | null>(null);
  const [historicalPrefilledFields, setHistoricalPrefilledFields] = useState<string[]>([]);

  useEffect(() => {
    if (!onLoadFinancialCondition || hasLoadedCertificateRef.current) {
      return;
    }

    hasLoadedCertificateRef.current = true;
    const requestId = ++certificateRequestIdRef.current;

    setLoadedCertificate(null);
    setIsLoadingCertificate(true);

    void onLoadFinancialCondition().then((response) => {
      if (requestId !== certificateRequestIdRef.current) {
        return;
      }

      if (!response) {
        setIsLoadingCertificate(false);
        return;
      }

      certificateVersionRef.current = response.version;

      const borrowerType = response.fop ? BorrowerType.SoleTrader : BorrowerType.LegalEntity;
      const certificate =
        historicalBody && typeof historicalBody === 'object'
          ? (historicalBody as TFinancialConditionCertificateDto)
          : response.body;
      const loadedFinancialStatementForm =
        borrowerType === BorrowerType.LegalEntity
          ? getFinancialStatementFormFromCertificate(certificate)
          : undefined;

      if (borrowerType === BorrowerType.LegalEntity && !loadedFinancialStatementForm) {
        form.reset(
          applyFinancialConditionCertificateGeneralInfo(
            getFinancialConditionDefaultValues(),
            response
          )
        );
        setIsLoadingCertificate(false);
        return;
      }

      if (!certificate) {
        form.reset(
          applyFinancialConditionCertificateGeneralInfo(
            getFinancialConditionDefaultValues(),
            response
          )
        );
        setIsLoadingCertificate(false);
        return;
      }

      setLoadedCertificate({
        requestId,
        borrowerType,
        certificate,
        response,
        financialStatementForm: loadedFinancialStatementForm,
      });
      loadedFinancialStatementFormRef.current = loadedFinancialStatementForm ?? null;
    });
  }, [form, historicalBody, onLoadFinancialCondition]);

  useEffect(() => {
    if (
      !loadedCertificate ||
      !isDictionariesReady ||
      loadedCertificate.requestId !== certificateRequestIdRef.current
    ) {
      return;
    }

    const values = applyFinancialConditionCertificateGeneralInfo(
      mergeFinancialStatementFormValues(
        deserializeFinancialConditionCertificate(
          loadedCertificate.certificate,
          loadedCertificate.borrowerType,
          banks,
          currencies,
          loadedCertificate.financialStatementForm
        ),
        loadedCertificate.localValues
      ),
      loadedCertificate.response
    );

    form.reset(values);
    setHistoricalPrefilledFields(
      isHistoricalSource
        ? collectHistoricalPrefilledFieldNames(values, HISTORICAL_READ_ONLY_FIELDS)
        : []
    );
    setLoadedCertificate(null);
    setIsLoadingCertificate(false);
  }, [banks, currencies, form, isDictionariesReady, isHistoricalSource, loadedCertificate]);

  useEffect(() => {
    const shouldReload =
      onLoadFinancialCondition &&
      isLegalEntity &&
      financialStatementForm &&
      loadedFinancialStatementFormRef.current !== financialStatementForm;

    if (!shouldReload) {
      return;
    }

    const localValues =
      loadedFinancialStatementFormRef.current === null ? undefined : form.getValues();
    loadedFinancialStatementFormRef.current = financialStatementForm;
    const requestId = ++certificateRequestIdRef.current;

    setLoadedCertificate(null);
    setIsLoadingCertificate(true);

    void onLoadFinancialCondition().then((response) => {
      if (requestId !== certificateRequestIdRef.current) {
        return;
      }

      if (!response) {
        setIsLoadingCertificate(false);
        return;
      }

      const certificate =
        historicalBody && typeof historicalBody === 'object'
          ? (historicalBody as TFinancialConditionCertificateDto)
          : response.body;

      certificateVersionRef.current = response.version;

      if (!certificate) {
        const values = {
          ...getFinancialConditionDefaultValues(financialStatementForm),
          financialStatementForm: financialStatementFormOption,
        };

        form.reset(
          applyFinancialConditionCertificateGeneralInfo(
            mergeFinancialStatementFormValues(values, localValues),
            response
          )
        );
        setIsLoadingCertificate(false);
        return;
      }

      setLoadedCertificate({
        requestId,
        borrowerType: response.fop ? BorrowerType.SoleTrader : BorrowerType.LegalEntity,
        certificate,
        response,
        financialStatementForm,
        localValues,
      });
    });
  }, [
    financialStatementForm,
    financialStatementFormOption,
    form,
    historicalBody,
    isLegalEntity,
    onLoadFinancialCondition,
  ]);

  return {
    certificateVersionRef,
    historicalPrefilledFields,
    isLoadingCertificate,
  };
};
